import { useCallback } from "react";

import {
  canvasTextSenderReady,
  deliverCanvasText,
} from "@/features/notepad/canvasTextDelivery";
import {
  selectIsNotepadNonInteractive,
  selectIsNotepadOpen,
  selectTypedTurnAdmitted,
  useNotepadStore,
} from "@/features/notepad/store/useNotepadStore";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useChatTrailStore } from "@/features/tutor/store/useChatTrailStore";
import {
  selectChipVisible,
  useScratchpadChipStore,
} from "@/features/tutor/store/useScratchpadChipStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { buildTutorSpeechSnapshot } from "@/features/tutor/utils/tutorSpeechSnapshot";

export const MAX_USER_MESSAGE_LENGTH = 4096;

export type SendFailure = "empty" | "too-long" | "disconnected" | "refused";

export type SendResult = { sent: true } | { sent: false; reason: SendFailure };

export interface SubmitScratchpadOptions {
  userMessage?: string;
}

export interface SendChatMessageDeps {
  handleInterrupt: () => void;
  submitScratchpad?: (
    options?: SubmitScratchpadOptions,
  ) => Promise<boolean> | boolean;
}

export const useSendChatMessage = ({
  handleInterrupt,
  submitScratchpad,
}: SendChatMessageDeps) => {
  const { emit } = useRuntimeContext();

  return useCallback(
    async (rawText: string): Promise<SendResult> => {
      const text = rawText.trim();
      // Exactly what the chip is promising. Gating on anything narrower lets
      // the bar advertise a drawing the send then leaves behind.
      const hasWork = selectChipVisible(useScratchpadChipStore.getState());

      if (!text && !hasWork) return { sent: false, reason: "empty" };
      if (text.length > MAX_USER_MESSAGE_LENGTH) {
        return { sent: false, reason: "too-long" };
      }
      if (!useWebsocketStore.getState().wsConnected) {
        return { sent: false, reason: "disconnected" };
      }

      const trail = useChatTrailStore.getState();

      const notepad = useNotepadStore.getState();
      if (selectIsNotepadOpen(notepad)) {
        if (selectIsNotepadNonInteractive(notepad)) {
          return { sent: false, reason: "refused" };
        }
        if (!selectTypedTurnAdmitted(notepad)) {
          return { sent: false, reason: "refused" };
        }
        if (notepad.notepadWs?.readyState !== WebSocket.OPEN) {
          return { sent: false, reason: "refused" };
        }
        // The gates above are all satisfied by the WS transport, which connects
        // ahead of the notepad opening. The canvas bridge only exists once
        // tldraw has mounted, so check it separately before anything is torn
        // down — a miss here has to keep the student's text.
        if (text && !canvasTextSenderReady()) {
          return { sent: false, reason: "refused" };
        }

        if (!text) {
          const submitted = await submitScratchpad?.();
          if (!submitted) return { sent: false, reason: "refused" };
          return { sent: true };
        }

        const canvasSnapshot = buildTutorSpeechSnapshot();
        trail.sealTutorTurn();
        handleInterrupt();
        emit("TURN_CHANGE", { ...canvasSnapshot, source: "chat" });
        window.dispatchEvent(new CustomEvent("notepad:cancel-close"));

        // The turn id only exists once TURN_CHANGE has been handled, and the
        // bridge refuses without one — so deliver here, and open the trail turn
        // only for a message something actually took.
        if (!deliverCanvasText(text)) {
          return { sent: false, reason: "refused" };
        }

        trail.appendStudentMessage(text);
        trail.beginTutorTurn();

        return { sent: true };
      }

      // Scratchpad work carries the whole turn, typed sentence included:
      // DISCUSS_CONTEXT and the text chunk each trigger a reply on their own,
      // and two of them would answer the student twice.
      if (hasWork) {
        const submitted = await submitScratchpad?.({ userMessage: text });
        // Six refusals live in submitPendingWork — a reply in flight, a closed
        // socket, blocked input. Reporting the send as done would clear the
        // student's typed message for one of them.
        if (!submitted) return { sent: false, reason: "refused" };

        // After the submit, never before: it emits TURN_CHANGE, which the
        // trail sync seals on, closing a turn opened any earlier.
        if (text) trail.appendStudentMessage(text);
        trail.beginTutorTurn();
        return { sent: true };
      }

      const speechSnapshot = buildTutorSpeechSnapshot();

      trail.sealTutorTurn();
      handleInterrupt();
      emit("TURN_CHANGE", speechSnapshot);

      trail.appendStudentMessage(text);
      trail.beginTutorTurn();

      const { inputMode, tutorAudioOn } = useChatSettingsStore.getState();
      emit("USER_TEXT_CHUNK", {
        text,
        source: "chat",
        inputMode,
        tutorAudioOn,
      });

      return { sent: true };
    },
    [emit, handleInterrupt, submitScratchpad],
  );
};
